import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, message } from 'antd';
import { Storyteller } from '../../types';
import { storytellersApi, CreateStorytellerRequest, UpdateStorytellerRequest } from '../../services/api/storytellers';
import { useAsyncOperation } from '../../hooks';

const { TextArea } = Input;

interface StorytellerModalProps {
  visible: boolean;
  storyId: string;
  storyteller: Storyteller | null;
  onClose: () => void;
  onSuccess: () => void;
}

const StorytellerModal: React.FC<StorytellerModalProps> = ({
  visible,
  storyId,
  storyteller,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { loading, execute } = useAsyncOperation();

  useEffect(() => {
    if (visible && storyteller) {
      form.setFieldsValue({
        name: storyteller.name,
        personality: storyteller.personality,
        style: storyteller.style,
        isActive: storyteller.isActive,
      });
    } else {
      form.resetFields();
    }
  }, [visible, storyteller, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const operation = storyteller
        ? () => storytellersApi.updateStoryteller(storyId, storyteller.id, values as UpdateStorytellerRequest)
        : () => storytellersApi.createStoryteller(storyId, values as CreateStorytellerRequest);

      const result = await execute(operation, {
        successMessage: storyteller ? 'Storyteller updated!' : 'Storyteller created!'
      });

      if (result) {
        onSuccess();
      }
    } catch (error) {
      // Form validation error
    }
  };

  return (
    <Modal
      title={storyteller ? 'Edit Storyteller' : 'Add Storyteller'}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={storyteller ? 'Update' : 'Create'}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter name' }]}
        >
          <Input placeholder="e.g. Mysterious Narrator" />
        </Form.Item>

        <Form.Item
          name="personality"
          label="Personality"
          rules={[{ required: true, message: 'Please enter personality' }]}
        >
          <TextArea rows={3} placeholder="e.g. Calm, wise, slightly dark and mysterious..." />
        </Form.Item>

        <Form.Item
          name="style"
          label="Storytelling Style"
          rules={[{ required: true, message: 'Please enter style' }]}
        >
          <TextArea rows={3} placeholder="e.g. Slow pacing, dramatic pauses, emphasizing emotional beats..." />
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StorytellerModal;
